import React, { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/roomShow.module.css';
import Loader from '../components/Loader';

const RoomShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [rents, setRents] = useState([]);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiFailed, setApiFailed] = useState(false);

  // Mock data for when API fails
  const getMockData = () => {
    // Generate different room scenarios
    const generateMockRoom = (roomId) => {
      const rooms = [
        {
          id: roomId,
          name: "Room 101",
          is_couple: true,
          key_number: 2,
          isNoticeGiven: false,
          setPaymentForBothGuardians: true,
          changeRentForBothGuardians: true
        },
        {
          id: roomId,
          name: "Room 1-2 bed flat",
          is_couple: true,
          key_number: 2,
          isNoticeGiven: true, // Notice already given
          setPaymentForBothGuardians: false,
          changeRentForBothGuardians: true
        },
        {
          id: roomId,
          name: "Flat 28",
          is_couple: true,
          key_number: 2,
          isNoticeGiven: false,
          setPaymentForBothGuardians: true,
          changeRentForBothGuardians: false
        },
        {
          id: roomId,
          name: "Flat 7",
          is_couple: true,
          key_number: 2,
          isNoticeGiven: false,
          setPaymentForBothGuardians: false,
          changeRentForBothGuardians: false
        }
      ];

      const roomIndex = (roomId - 1) % rooms.length;
      return rooms[roomIndex];
    };

    const mockRoom = generateMockRoom(parseInt(id));

    // Generate different property scenarios
    const generateMockProperty = (roomId) => {
      const properties = [
        {
          id: 1,
          property_id: "PL282103719",
          name: "12 Savage Road",
          status_text: "Ended",
          address_1: "12 Savage Road",
          postcode: "PL5 1BP",
          date_taken_on: "2022-06-06",
          date_taken_back: "2030-12-31"
        },
        {
          id: 2,
          property_id: "PROP002",
          name: "46 Plummer Road",
          status_text: "Active",
          address_1: "46 Plummer Road, London",
          postcode: "SW1A 2BB",
          date_taken_on: "2024-03-01",
          date_taken_back: "2025-03-01"
        },
        {
          id: 3,
          property_id: "PROP003",
          name: "Flat 9, 44 Garden Walk",
          status_text: "Active",
          address_1: "Flat 9, 44 Garden Walk, London",
          postcode: "SW1A 3CC",
          date_taken_on: "2024-02-15",
          date_taken_back: null
        },
        {
          id: 4,
          property_id: "PROP004",
          name: "Flat 15, 22 High Street",
          status_text: "Active",
          address_1: "Flat 15, 22 High Street, London",
          postcode: "SW1A 4DD",
          date_taken_on: "2024-01-10",
          date_taken_back: "2026-01-10"
        }
      ];

      const propertyIndex = (roomId - 1) % properties.length;
      return properties[propertyIndex];
    };

    const mockProperty = generateMockProperty(parseInt(id));

    // Generate different rent scenarios
    const generateMockRents = (roomId) => {
      const rentScenarios = [
        // Scenario 1: Both guardians placed, can set payment and change rent
        [
          {
            id: 1,
            guardian: {
              id: 1,
              full_name: "John Smith",
              unique_guardian_number: "G123456",
              status: { name: "Property Assigned" },
              email: "john.smith@email.com"
            },
            license_fee: 750,
            damage_security_payment: 500,
            payment: 1250,
            status: "active"
          },
          {
            id: 2,
            guardian: {
              id: 2,
              full_name: "Jane Doe",
              unique_guardian_number: "G789012",
              status: { name: "Property Assigned" },
              email: "jane.doe@email.com"
            },
            license_fee: 750,
            damage_security_payment: 500,
            payment: 1250,
            status: "active"
          }
        ],
        // Scenario 2: One guardian placed, one payment pending
        [
          {
            id: 1,
            guardian: {
              id: 1,
              full_name: "Alice Johnson",
              unique_guardian_number: "G345678",
              status: { name: "Placed" },
              email: "alice.johnson@email.com"
            },
            license_fee: 800,
            damage_security_payment: 600,
            payment: 1400,
            status: "active"
          },
          {
            id: 2,
            guardian: {
              id: 2,
              full_name: "Bob Wilson",
              unique_guardian_number: "G901234",
              status: { name: "Payment Pending" },
              email: "bob.wilson@email.com"
            },
            license_fee: 0,
            damage_security_payment: 0,
            payment: 0,
            status: "pending"
          }
        ],
        // Scenario 3: Both guardians in different statuses
        [
          {
            id: 1,
            guardian: {
              id: 1,
              full_name: "Charlie Brown",
              unique_guardian_number: "G567890",
              status: { name: "Awaiting Key Collection" },
              email: "charlie.brown@email.com"
            },
            license_fee: 900,
            damage_security_payment: 700,
            payment: 1600,
            status: "active"
          },
          {
            id: 2,
            guardian: {
              id: 2,
              full_name: "Diana Prince",
              unique_guardian_number: "G123789",
              status: { name: "Vetted" },
              email: "diana.prince@email.com"
            },
            license_fee: 900,
            damage_security_payment: 700,
            payment: 1600,
            status: "active"
          }
        ],
        // Scenario 4: One guardian with NTQ status
        [
          {
            id: 1,
            guardian: {
              id: 1,
              full_name: "Eve Adams",
              unique_guardian_number: "G456123",
              status: { name: "Placed" },
              email: "eve.adams@email.com"
            },
            license_fee: 850,
            damage_security_payment: 550,
            payment: 1400,
            status: "active"
          },
          {
            id: 2,
            guardian: {
              id: 2,
              full_name: "Frank Miller",
              unique_guardian_number: "G789456",
              status: { name: "NTQ" },
              email: "frank.miller@email.com"
            },
            license_fee: 0,
            damage_security_payment: 0,
            payment: 0,
            status: "ntq"
          }
        ]
      ];

      const rentIndex = (roomId - 1) % rentScenarios.length;
      return rentScenarios[rentIndex];
    };

    const mockRents = generateMockRents(parseInt(id));

    return {
      room: mockRoom,
      property: mockProperty,
      rents: mockRents
    };
  };

  useEffect(() => {
    loadRoomData();
  }, [id]);

  const loadRoomData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Simulate API call
      const response = await fetch(`/api/rooms/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to load room data');
      }

      const data = await response.json();
      setRoom(data.room);
      setRents(data.rents || []);
      setProperty(data.property);
      setApiFailed(false);
    } catch (error) {
      console.error('Error loading room data:', error);
      setError(error.message);
      setApiFailed(true);
      
      // Use mock data as fallback
      const mockData = getMockData();
      setRoom(mockData.room);
      setRents(mockData.rents);
      setProperty(mockData.property);
    } finally {
      setLoading(false);
    }
  };

  const handleMove = () => {
    navigate(`/rooms/${id}/manage`);
  };

  const handleGiveNotice = () => {
    // Laravel-compatible nested route: /rents/:rentId/notice-to-quit/create
    if (rents && rents.length > 0 && rents[0]?.id) {
      navigate(`/rents/${rents[0].id}/notice-to-quit/create`);
    } else {
      toast.error('No rent found for this room');
    }
  };

  const handleSetPayment = () => {
    // Laravel route: /rents/{room}/payments/edit
    navigate(`/rents/${id}/payments/edit`);
  };

  const handleChangeRent = () => {
    // Laravel route: /rents/{room}/payments/edit
    navigate(`/rents/${id}/payments/edit`);
  };

  const handleGuardianClick = (guardianId) => {
    navigate(`/guardians/${guardianId}`);
  };

  const handleFinishNTQ = (rentId) => {
    toast.success('NTQ finished successfully');
  };

  const handleManageNTQ = (rentId) => {
    navigate(`/rents/${rentId}/notice-to-quit/manage`);
  };

  const handleUploadDocuments = (guardianId) => {
    toast.info('Upload documents functionality would be implemented here');
  };

  const handleProcessPayment = (guardianId) => {
    toast.info('Process payment functionality would be implemented here');
  };

  const handleArrangeKeyCollection = (guardianId) => {
    toast.info('Arrange key collection functionality would be implemented here');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-GB');
    } catch (error) {
      return dateTimeString;
    }
  };

  const shouldDisplayDate = (dateString) => {
    if (!dateString) return '-';
    return formatDate(dateString);
  };

  const getStatusColor = (statusName) => {
    const statusColors = {
      'Active': '#28a745',
      'Documents Required': '#ffc107',
      'Property Assigned': '#17a2b8',
      'Payment Pending': '#fd7e14',
      'Awaiting Key Collection': '#6f42c1',
      'Placed': '#28a745',
      'NTQ': '#dc3545'
    };
    return statusColors[statusName] || '#6c757d';
  };

  if (loading) return <Loader />;
  if (!room) return <div className={styles.errorContainer}>Room not found</div>;

  return (
    <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
      <div className={styles.container}>
        {/* Breadcrumbs */}
        <div className={styles.breadcrumbContainer}>
          <nav aria-label="breadcrumb">
            <ol className={styles.breadcrumb}>
              <li className={styles.breadcrumbItem}>
                <Link to="/guardians">Guardians</Link>
              </li>
              <li className={styles.breadcrumbItem}>
                <Link to="/rooms/couples">Couples</Link>
              </li>
              <li className={`${styles.breadcrumbItem} ${styles.active}`}>
                Room {room.name}
              </li>
            </ol>
          </nav>
        </div>

        <div className={styles.row}>
          <div className={styles.colMd12}>
            {/* Couple Overview */}
            <div className={styles.row}>
              <div className={styles.colMd12}>
                <div className={`${styles.panelTitle} ${styles.titleStandard} ${styles.marginDown}`}>
                  <h3>Couple Overview</h3>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className={styles.row}>
              <div className={styles.colMd12}>
                <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                  <h3>Actions</h3>
                </div>
                <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                  {/* Move - Always shown if user has permission */}
                  <button 
                    className={`${styles.btnHead} ${styles.mr10}`}
                    onClick={handleMove}
                    title="Move guardians to different room"
                  >
                    Move
                  </button>

                  {/* Give Notice - Shown if no notice given and guardians are placed */}
                  {!room.isNoticeGiven && rents.some(rent => rent.guardian.status.name === 'Placed') && (
                    <button 
                      className={`${styles.btnHead} ${styles.mr10}`}
                      onClick={handleGiveNotice}
                      title="Give notice to quit"
                    >
                      Give Notice
                    </button>
                  )}

                  {/* Set Payment - Shown if all guardians have status "Property Assigned" */}
                  {rents.length > 0 && rents.every(rent => rent.guardian.status.name === 'Property Assigned') && (
                    <button 
                      className={`${styles.btnHead} ${styles.mr10}`}
                      onClick={handleSetPayment}
                      title="Set payment for both guardians"
                    >
                      Set payment
                    </button>
                  )}

                  {/* Change Rent - Shown if all guardians can have changed payments */}
                  {rents.length > 0 && rents.every(rent => {
                    const status = rent.guardian.status.name;
                    return status === 'Payment Pending' || 
                           status === 'Awaiting Key Collection' || 
                           status === 'Placed' || 
                           status === 'Vetted';
                  }) && (
                    <button 
                      className={`${styles.btnHead} ${styles.mr10}`}
                      onClick={handleChangeRent}
                      title="Change rent for both guardians"
                    >
                      Change rent
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Guardian Information */}
            <div className={styles.row}>
              {rents.map((rent, key) => (
                <div key={rent.id} className={`${styles.colXs12} ${styles.colSm6}`}>
                  <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                    <h3>Guardian #{key + 1}</h3>
                  </div>
                  <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                    <dl className={`${styles.list} ${styles.greenList} ${styles.greenListWithWidth}`}>
                      <dt>Name:</dt>
                      <dd>
                        <a 
                          href={`/guardians/${rent.guardian.id}`}
                          className={styles.guardianLink}
                          onClick={(e) => {
                            e.preventDefault();
                            handleGuardianClick(rent.guardian.id);
                          }}
                        >
                          {rent.guardian.full_name} ({rent.guardian.unique_guardian_number})
                        </a>
                      </dd>
                      <dt>Email:</dt>
                      <dd>{rent.guardian.email}</dd>
                      <dt>Status:</dt>
                      <dd>
                        <span 
                          className={styles.statusBadge}
                          style={{ backgroundColor: getStatusColor(rent.guardian.status.name) }}
                        >
                          {rent.guardian.status.name}
                        </span>
                      </dd>
                      <dt>License Fee:</dt>
                      <dd>£{rent.license_fee}</dd>
                      <dt>Key Payment:</dt>
                      <dd>£{rent.damage_security_payment}</dd>
                      <dt>Total Payment:</dt>
                      <dd><b>£{rent.payment}</b></dd>
                    </dl>

                    {/* NTQ Actions */}
                    {rent.guardian.status.name === 'NTQ' && (
                      <div className={styles.ntqActions}>
                        <button 
                          className={`${styles.btn} ${styles.btnWarning}`}
                          onClick={() => handleFinishNTQ(rent.id)}
                        >
                          Finish NTQ
                        </button>
                        <button 
                          className={`${styles.btn} ${styles.btnInfo}`}
                          onClick={() => handleManageNTQ(rent.id)}
                        >
                          Manage NTQ
                        </button>
                      </div>
                    )}

                    {/* Action Buttons based on status */}
                    {rent.guardian.status.name === 'Documents Required' && (
                      <button 
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={() => handleUploadDocuments(rent.guardian.id)}
                      >
                        Upload Documents
                      </button>
                    )}

                    {rent.guardian.status.name === 'Payment Pending' && (
                      <button 
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={() => handleProcessPayment(rent.guardian.id)}
                      >
                        Process Payment
                      </button>
                    )}

                    {rent.guardian.status.name === 'Awaiting Key Collection' && (
                      <button 
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={() => handleArrangeKeyCollection(rent.guardian.id)}
                      >
                        Arrange Key Collection
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Property Information */}
            {property && (
              <div className={styles.row}>
                <div className={styles.colMd12}>
                  <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                    <h3>Property Information</h3>
                  </div>
                  <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                    <div className={styles.colSm6}>
                      <dl className={`${styles.list} ${styles.greenList} ${styles.greenListWithWidth}`}>
                        <dt>Property ID:</dt>
                        <dd>{property.property_id}</dd>
                        <dt>Property Name:</dt>
                        <dd>{property.name}</dd>
                        <dt>Property Status:</dt>
                        <dd>{property.status_text}</dd>
                        <dt>Address:</dt>
                        <dd>{property.address_1}</dd>
                        <dt>Postcode:</dt>
                        <dd>{property.postcode}</dd>
                        <dt>Lease Dates:</dt>
                        <dd>
                          {shouldDisplayDate(property.date_taken_on)}
                          {property.date_taken_back && ` to ${shouldDisplayDate(property.date_taken_back)}`}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomShow; 